import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getAliceAgentApp } from 'client/api/services/aliceAgentApps';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceExternalAppForm';

import { AliceExternalAppAccountLinking } from './AliceExternalAppAccountLinking';
import { AliceExternalAppEditPage } from './AliceExternalAppEditPage';
import { AliceExternalAppLayout } from './AliceExternalAppLayout';

const t = i18n(keyset);

interface Props {
    skillId: string;
}

const PROCESSING_POLL_INTERVAL_MS = 3000;

/**
 * Loads the External-agent DTO, owns refresh + status polling. While the draft sits in
 * `mcpProcessing` we re-fetch every ~3s so the form picks up the post-worker manifest without
 * a page reload.
 *
 * The page chrome (sidebar, version switcher, header, banners) lives in
 * `<AliceExternalAppLayout>` — `<Routes>` is rendered inside it so navigating between
 * Settings and Authorization swaps only the inner content (mirrors `AliceSkillAppPage`).
 */
export const AliceExternalAppPageContainer: FC<Props> = ({ skillId }) => {
    const { add: toasterAdd } = useToaster();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [app, setApp] = useState<AliceAgentAppDTO>();
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchApp = useCallback(async () => {
        try {
            const next = await getAliceAgentApp(skillId);
            setApp(next);
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-load-error');
            toasterAdd({ title: message, theme: 'danger', name: 'LOAD_ALICE_EXTERNAL_APP' });
        } finally {
            setIsLoading(false);
        }
    }, [skillId, toasterAdd]);

    useEffect(() => {
        fetchApp();
    }, [fetchApp]);

    useEffect(() => {
        if (!app) return;
        if (app.draft.status !== 'mcpProcessing') {
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
            return;
        }
        pollTimer.current = setTimeout(fetchApp, PROCESSING_POLL_INTERVAL_MS);
        return () => {
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
        };
    }, [app, fetchApp]);

    if (isLoading && !app) return <PageLoader />;
    if (!app) return <Navigate to={routes.app.aliceApps} replace />;

    // Active version drives the version-switcher highlight in the layout's sidebar. Authorization
    // exists only under draft, so visiting it still highlights "Draft" in the switcher.
    const isDraftView = !location.pathname.includes('/published/');

    // Account-linking is editable only while the draft is in `inDevelopment` — the backend
    // service rejects link/unlink in `mcpProcessing`/`mcpFailed`/`reviewApproved`.
    const accountLinkingDisabled = app.draft.status !== 'inDevelopment';

    return (
        <AliceExternalAppLayout app={app} isDraft={isDraftView} onSaved={fetchApp}>
            <Routes>
                <Route
                    path="draft/settings"
                    element={<AliceExternalAppEditPage app={app} isDraft onSaved={fetchApp} />}
                />
                <Route
                    path="draft/authorization"
                    element={
                        <AliceExternalAppAccountLinking
                            app={app}
                            onSaved={fetchApp}
                            disabled={accountLinkingDisabled}
                        />
                    }
                />
                <Route
                    path="published/settings"
                    element={
                        app.published ? (
                            <AliceExternalAppEditPage app={app} isDraft={false} onSaved={fetchApp} />
                        ) : (
                            <Navigate to="../draft/settings" replace />
                        )
                    }
                />
                <Route path="*" element={<Navigate to="draft/settings" replace />} />
            </Routes>
        </AliceExternalAppLayout>
    );
};
